import { FadeHorizontalScroll } from "@/components/horizontal-scroll/FadeHorizontalScroll";
import {
  MdsCarouselItem,
  MdsCarouselItemVariant,
  MdsItemListCarouselRowData,
} from "@/design-system/components/item-list/types";
import { MdsText, MdsTextSize } from "@/design-system/components/text";
import { MdsTextOverflow } from "@/design-system/components/text/types";
import { css, cx } from "@/domains/emotion";
import { observer } from "mobx-react-lite";
import { FC } from "react";
import { MDSButton } from "@/mds/components/buttons/button";
import styled from "@emotion/styled";
import { mdsColors } from "@/design-system/foundations/colors";

export interface MdsItemListCarouselRowProps {
  data: MdsItemListCarouselRowData;
  className?: string;
}

export const MdsItemListCarouselRow = observer<MdsItemListCarouselRowProps>(
  ({ data, className }) => {
    return (
      <FadeHorizontalScroll className={cx(carouselStyles, className)}>
        {data.payload.items
          .filter(item => !item.isHidden)
          .map(item => (
            <MdsItemListCarouselRowItem key={item.id} {...item} />
          ))}
      </FadeHorizontalScroll>
    );
  }
);

const MdsItemListCarouselRowItem: FC<MdsCarouselItem> = ({
  id: itemId,
  icon,
  primaryLabel,
  secondaryLabel,
  variant,
  onClick,
}) => {
  return (
    <StyledButton variant={variant} onPress={() => onClick({ itemId })}>
      <Inner>
        {variant === MdsCarouselItemVariant.Item || variant === MdsCarouselItemVariant.Other ? (
          <IconWrapper>{icon()}</IconWrapper>
        ) : (
          icon()
        )}
        <CenterVertically>
          <PrimaryLabel size={MdsTextSize.Small} overflow={MdsTextOverflow.Ellipsis}>
            {primaryLabel}
          </PrimaryLabel>
          {secondaryLabel && (
            <SecondaryLabel size={MdsTextSize.XSmall} overflow={MdsTextOverflow.Ellipsis}>
              {secondaryLabel}
            </SecondaryLabel>
          )}
        </CenterVertically>
      </Inner>
    </StyledButton>
  );
};

const carouselStyles = css({
  gap: "16px",
  padding: "8px",
  maxWidth: "100%",
});

const Inner = styled.div({
  display: "flex",
  gap: "12px",
  minWidth: 200,
});

const StyledButton = styled(MDSButton)<{ variant?: MdsCarouselItemVariant }>(
  ({ theme, variant }) => ({
    borderRadius: theme.borderRadius.mediumLarge,
    backgroundColor:
      variant === MdsCarouselItemVariant.Item || variant === MdsCarouselItemVariant.Collection
        ? theme.colors.white
        : theme.colors.grey.x25,
  })
);

const PrimaryLabel = styled(MdsText)(({ theme }) => ({
  color: theme.colors.grey.x600,
  fontSize: theme.fontSizes.small,
  fontWeight: theme.fontWeights.medium,
  lineHeight: theme.lineHeights.small,
}));

const SecondaryLabel = styled(MdsText)(({ theme }) => ({
  color: theme.colors.grey.x500,
  fontSize: theme.fontSizes.small,
  fontWeight: theme.fontWeights.regular,
  lineHeight: theme.lineHeights.small,
}));

const IconWrapper = styled.div<{ variant?: MdsCarouselItemVariant }>(({ theme, variant }) => ({
  width: 40,
  height: 40,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor:
    variant === MdsCarouselItemVariant.Other ? theme.colors.grey.x100 : theme.colors.grey.x50,
  padding: "10px",
  borderRadius: theme.borderRadius.maximum,

  "& svg": {
    height: 20,
    width: 20,
    color: mdsColors().grey.x400,
  },
}));

const CenterVertically = styled.div({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
});
